import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getDevProjects } from '../../features/projects/devprojectSlice';
import {
  Typography,
  Card,
  Empty,
  Select,
  Space,
  Tag,
  Button,
  Divider,
  Tooltip,
} from 'antd';
import {
  CodeOutlined,
  EyeOutlined,
  GlobalOutlined,
  GithubOutlined,
  CalendarOutlined,
  FileTextOutlined,
  SearchOutlined,
} from '@ant-design/icons';

// Import the reusable hook and utility
import { useRichTextStyles } from '../../utils/useRichTextStyles';
import { processContentForMedia } from '../../utils/mediaContentProcessor';

const { Title, Text } = Typography;
const { Option } = Select;

const PreviewDevContent = ({ onEditContent }) => {
  const dispatch = useDispatch();
  const { devprojects, loading } = useSelector((state) => state.devprojects);
  const [selectedProject, setSelectedProject] = useState(null);
  const [searchValue, setSearchValue] = useState('');

  // Use the reusable hook for rich text styles and carousel functionality
  useRichTextStyles();

  useEffect(() => {
    dispatch(getDevProjects());
  }, [dispatch]);

  // Add useEffect to handle selectedProject updates when devprojects changes
  useEffect(() => {
    if (selectedProject && devprojects.length > 0) {
      // Find the updated version of the selected project
      const updatedProject = devprojects.find(
        (p) => p._id === selectedProject._id
      );
      if (updatedProject) {
        setSelectedProject(updatedProject);
      } else {
        // Project was deleted, clear selection
        setSelectedProject(null);
      }
    }
  }, [devprojects, selectedProject]);

  // Helper function to check if content is truly empty
  const isContentEmpty = (content) => {
    if (!content) return true;

    // Remove HTML tags and check if any meaningful content remains
    const textContent = content
      .replace(/<[^>]*>/g, '') // Remove all HTML tags
      .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
      .trim(); // Remove whitespace

    return textContent === '';
  };

  // Filter projects that have content
  const projectsWithContent = devprojects.filter(
    (project) =>
      project.body && project.body.trim() && !isContentEmpty(project.body)
  );

  // Filter projects based on search
  const filteredProjects = projectsWithContent.filter((project) => {
    if (!searchValue) return true;

    const searchTerm = searchValue.toLowerCase();
    return (
      project.name.toLowerCase().includes(searchTerm) ||
      project.desc.toLowerCase().includes(searchTerm) ||
      project.type.toLowerCase().includes(searchTerm) ||
      (project.stacks &&
        project.stacks.some((stack) =>
          stack.toLowerCase().includes(searchTerm)
        ))
    );
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusColor = (status) => {
    return status ? 'green' : 'orange';
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'web':
        return '🌐';
      case 'mobile':
        return '📱';
      case 'desktop':
        return '🖥️';
      default:
        return '💻';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'web':
        return 'Web Application';
      case 'mobile':
        return 'Mobile App';
      case 'desktop':
        return 'Desktop Application';
      default:
        return 'Software Project';
    }
  };

  const handleProjectChange = (projectId) => {
    const project = devprojects.find((p) => p._id === projectId);
    setSelectedProject(project);
  };

  const handleSearchChange = (value) => {
    setSearchValue(value);
    // Clear selection if current project doesn't match search
    if (selectedProject && value) {
      const searchTerm = value.toLowerCase();
      const projectMatches =
        selectedProject.name.toLowerCase().includes(searchTerm) ||
        selectedProject.desc.toLowerCase().includes(searchTerm) ||
        selectedProject.type.toLowerCase().includes(searchTerm) ||
        (selectedProject.stacks &&
          selectedProject.stacks.some((stack) =>
            stack.toLowerCase().includes(searchTerm)
          ));
      if (!projectMatches) {
        setSelectedProject(null);
      }
    }
  };

  return (
    <div className='max-w-5xl mx-auto'>
      {/* Header */}
      <div className='mb-6'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center space-x-3'>
            <EyeOutlined className='text-2xl text-blue-600' />
            <div>
              <Title level={4} className='m-0'>
                Preview Development Documentation
              </Title>
              <Text className='text-gray-600'>
                View detailed documentation and technical content for your
                projects
              </Text>
            </div>
          </div>
          <div className='flex items-center space-x-2'>
            <FileTextOutlined className='text-blue-600' />
            <Text className='text-sm text-gray-600'>
              {projectsWithContent.length} project
              {projectsWithContent.length !== 1 ? 's' : ''} with documentation
            </Text>
          </div>
        </div>
      </div>

      {projectsWithContent.length === 0 ? (
        <Card className='text-center py-12'>
          <Empty
            image={<CodeOutlined className='text-6xl text-gray-400' />}
            description={
              <div>
                <Title level={4} className='text-gray-500 mb-2'>
                  No Documentation Available
                </Title>
                <Text className='text-gray-400'>
                  No development projects have detailed documentation yet.
                </Text>
              </div>
            }
          />
        </Card>
      ) : (
        <>
          {/* Project Selector with Search */}
          <Card className='mb-6 bg-blue-50 border-blue-200'>
            <div className='space-y-4'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center space-x-4'>
                  <CodeOutlined className='text-2xl text-blue-600' />
                  <div>
                    <Text className='text-blue-800 font-medium block'>
                      Select Project to Preview
                    </Text>
                    <Text className='text-blue-600 text-sm'>
                      {searchValue ? (
                        <>
                          Showing {filteredProjects.length} of{' '}
                          {projectsWithContent.length} projects
                        </>
                      ) : (
                        <>
                          Choose from {projectsWithContent.length} project
                          {projectsWithContent.length !== 1 ? 's' : ''} with
                          documentation
                        </>
                      )}
                    </Text>
                  </div>
                </div>
              </div>

              {/* Search and Select Row */}
              <div className='flex items-center space-x-4'>
                <div className='flex-1 min-w-0'>
                  {' '}
                  {/* Add min-w-0 to prevent overflow */}
                  <Select
                    showSearch
                    placeholder='Search and select a project...'
                    style={{ width: '100%' }}
                    onChange={handleProjectChange}
                    onSearch={handleSearchChange}
                    size='large'
                    value={selectedProject?._id}
                    filterOption={false}
                    searchValue={searchValue}
                    suffixIcon={<SearchOutlined className='text-blue-600' />}
                    optionLabelProp='label' // This prevents the full Option content from appearing in search bar
                    notFoundContent={
                      searchValue ? (
                        <div className='text-center py-4'>
                          <SearchOutlined className='text-2xl text-gray-400 mb-2' />
                          <div className='text-gray-500'>
                            No projects found matching "{searchValue}"
                          </div>
                          <div className='text-xs text-gray-400 mt-1'>
                            Try searching by project name, description, type, or
                            technologies
                          </div>
                        </div>
                      ) : null
                    }
                  >
                    {filteredProjects.map((project) => (
                      <Option
                        key={project._id}
                        value={project._id}
                        label={project.name} // This is what shows in the search bar when selected
                      >
                        <div className='flex items-center justify-between py-1'>
                          <div className='flex items-center space-x-2'>
                            <span>{getTypeIcon(project.type)}</span>
                            <span className='font-medium'>{project.name}</span>
                            <Tag size='small' color='blue'>
                              {getTypeLabel(project.type)}
                            </Tag>
                          </div>
                          <div className='flex items-center space-x-2'>
                            <Tag
                              size='small'
                              color={getStatusColor(project.status)}
                            >
                              {project.status ? 'Completed' : 'In Progress'}
                            </Tag>
                          </div>
                        </div>
                        <div className='text-xs text-gray-500 mt-1 ml-6 truncate'>
                          {project.desc}
                        </div>
                        {project.stacks && project.stacks.length > 0 && (
                          <div className='flex flex-wrap gap-1 mt-2 ml-6'>
                            {project.stacks.slice(0, 4).map((stack, index) => (
                              <Tag key={index} size='small' className='text-xs'>
                                {stack}
                              </Tag>
                            ))}
                            {project.stacks.length > 4 && (
                              <Tag size='small' className='text-xs'>
                                +{project.stacks.length - 4} more
                              </Tag>
                            )}
                          </div>
                        )}
                      </Option>
                    ))}
                  </Select>
                </div>

                {searchValue && (
                  <Button
                    onClick={() => {
                      setSearchValue('');
                      setSelectedProject(null);
                    }}
                    className='text-blue-600 hover:text-blue-800 flex-shrink-0'
                  >
                    Clear Search
                  </Button>
                )}
              </div>
            </div>
          </Card>

          {/* Content Preview */}
          {selectedProject ? (
            <div className='space-y-6'>
              {/* Project Header */}
              <Card className='bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200'>
                <div className='flex items-start space-x-6'>
                  <div className='flex-shrink-0'>
                    {selectedProject.url ? (
                      <img
                        src={selectedProject.url}
                        alt={selectedProject.name}
                        className='w-24 h-24 object-cover rounded-lg shadow-md'
                      />
                    ) : (
                      <div className='w-24 h-24 bg-blue-200 rounded-lg flex items-center justify-center shadow-md'>
                        <CodeOutlined className='text-3xl text-blue-600' />
                      </div>
                    )}
                  </div>

                  <div className='flex-1'>
                    <div className='flex items-center space-x-3 mb-3'>
                      <Title level={3} className='m-0 text-blue-900'>
                        {selectedProject.name}
                      </Title>
                      <span className='text-2xl'>
                        {getTypeIcon(selectedProject.type)}
                      </span>
                      <Tag color={getStatusColor(selectedProject.status)}>
                        {selectedProject.status ? 'Completed' : 'In Progress'}
                      </Tag>
                      <Tag color='blue'>
                        {getTypeLabel(selectedProject.type)}
                      </Tag>
                    </div>

                    <Text className='text-gray-700 text-base mb-4 block'>
                      {selectedProject.desc}
                    </Text>

                    <div className='flex items-center space-x-4 text-sm text-gray-600'>
                      <div className='flex items-center space-x-1'>
                        <CalendarOutlined />
                        <span>{formatDate(selectedProject.createdAt)}</span>
                      </div>

                      {selectedProject.link && (
                        <Tooltip title='Live Demo'>
                          <Button
                            type='link'
                            size='small'
                            icon={<GlobalOutlined />}
                            onClick={() =>
                              window.open(selectedProject.link, '_blank')
                            }
                            className='text-blue-600 hover:text-blue-800 p-0'
                          >
                            Live Demo
                          </Button>
                        </Tooltip>
                      )}

                      {selectedProject.github && (
                        <Tooltip title='GitHub Repository'>
                          <Button
                            type='link'
                            size='small'
                            icon={<GithubOutlined />}
                            onClick={() =>
                              window.open(selectedProject.github, '_blank')
                            }
                            className='text-gray-600 hover:text-gray-800 p-0'
                          >
                            GitHub
                          </Button>
                        </Tooltip>
                      )}
                    </div>
                  </div>

                  <div className='flex-shrink-0'>
                    <Button
                      type='primary'
                      icon={<CodeOutlined />}
                      onClick={() =>
                        onEditContent && onEditContent(selectedProject)
                      }
                      className='bg-blue-600 hover:bg-blue-700'
                    >
                      Edit Documentation
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Tech Stack */}
              {selectedProject.stacks && selectedProject.stacks.length > 0 && (
                <Card>
                  <Title level={5} className='mb-3 flex items-center'>
                    <CodeOutlined className='mr-2 text-blue-600' />
                    Technology Stack
                  </Title>
                  <div className='flex flex-wrap gap-2'>
                    {selectedProject.stacks.map((stack, index) => (
                      <Tag
                        key={index}
                        className='bg-blue-50 text-blue-700 border-blue-200'
                      >
                        {stack}
                      </Tag>
                    ))}
                  </div>
                </Card>
              )}

              {/* Technical Documentation */}
              <Card>
                <div className='flex items-center justify-between mb-4'>
                  <Title level={5} className='m-0 flex items-center'>
                    <FileTextOutlined className='mr-2 text-blue-600' />
                    Technical Documentation
                  </Title>
                  <Text className='text-sm text-gray-500'>
                    {selectedProject.body?.length || 0} characters
                  </Text>
                </div>

                <Divider className='my-4' />

                <div
                  className='rich-text-content'
                  style={{
                    lineHeight: '1.7',
                    fontSize: '16px',
                    maxWidth: 'none',
                  }}
                  dangerouslySetInnerHTML={{
                    __html:
                      processContentForMedia(selectedProject.body) ||
                      '<p class="text-gray-500 italic">No documentation available</p>',
                  }}
                />
              </Card>
            </div>
          ) : (
            <Card className='text-center py-12 bg-gray-50'>
              <EyeOutlined className='text-4xl text-gray-400 mb-4' />
              <Title level={4} className='text-gray-500 mb-2'>
                {searchValue
                  ? 'No Projects Found'
                  : 'Select a Project to Preview'}
              </Title>
              <Text className='text-gray-400'>
                {searchValue ? (
                  <>
                    No projects match your search "{searchValue}". Try different
                    keywords.
                  </>
                ) : (
                  <>
                    Choose a project from the dropdown above to view its
                    detailed documentation
                  </>
                )}
              </Text>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default PreviewDevContent;
